import { SELF_EMPLOYED, W2 } from "../../constants/wage_forms";
import {
  INDIVIDUAL_NAME_PROPS,
  MARITAL_STATUS_ENUM,
  ADDRESS_PROPS,
  ADDRESS_STATE_PROPS,
  INCOME_TYPE_ENUM,
  OWNERSHIP_ENUM,
  CREDIT_SCORE_VALUES
} from "../constants";
import { YES } from "../../constants/yes_no";
import { OWN, RENT } from "../../constants/renting_or_owning";
import { payment_to_year } from "../../calculations/income";
import { representative_mid_score } from "../../calculations/applicant";
import { SEASONAL_INCOME, OTHER_INCOME } from "../../constants/additional_income_types";
import { mismoBool } from "../mismo_util";

function toArray(obj) {
  return Array.isArray(obj) ? obj : [obj];
}

function getNestedValue(obj, path) {
  const parts = path.split(".");
  let currObj = obj;

  for (let i = 0; i < parts.length; i++) {
    const child = currObj[parts[i]];

    if (i === (parts.length - 1)) {
      return child;
    }

    if (!obj || (typeof child !== "object")) {
      return undefined;
    }

    currObj = child;
  }
}

export function importName(nameObj, { borrower }) {
  for (const [key, name] of Object.entries(INDIVIDUAL_NAME_PROPS)) {
    if (nameObj[name]) {
      borrower[key] = nameObj[name];
    }
  }
}

export function importContactPoints(obj, { borrower }) {
  if (!obj.CONTACT_POINT) {
    return;
  }

  const contactPoints = toArray(obj.CONTACT_POINT);
  const phones = [];

  for (const contactPoint of contactPoints) {
    const email = getNestedValue(
      contactPoint,
      "CONTACT_POINT_EMAIL.ContactPointEmailValue"
    );
    if (email) {
      borrower.email = email;
    }

    const phone = getNestedValue(
      contactPoint,
      "CONTACT_POINT_TELEPHONE.ContactPointTelephoneValue"
    );
    // An empty <ContactPointTelephoneValue/> converts to {} (truthy), not "".
    // Only keep real text, or the phone formatter crashes on the object.
    if (typeof phone === "string" && phone.trim()) {
      const detail = getNestedValue(
        contactPoint,
        "CONTACT_POINT_DETAIL.ContactPointRoleType"
      );
      phones.push({ phone, detail });
    }
  }

  if (phones.length > 0) {
    console.log("Found phones for " + borrower.first_name, phones);

    let foundCell, foundHome, foundWork = false;
    const noMatch = [];

    for (const { phone, detail } of phones) {
      if (detail) {
        const detailLowerCase = detail.toLowerCase();

        if (detailLowerCase.indexOf("mobile") !== -1
            || detailLowerCase.indexOf("cell") !== -1) {
          borrower.cell_phone = phone;
          foundCell = true;
        } else if (detailLowerCase.indexOf("home") !== -1) {
          borrower.home_phone = phone;
          foundHome = true;
        } else if (detailLowerCase.indexOf("work") !== -1) {
          borrower.work_phone = phone;
          foundWork = true;
        } else {
          noMatch.push({ phone, detail });
        }
      } else {
        noMatch.push({ phone });
      }
    }

    if (noMatch.length > 0) {
      console.warn(
        "Couldn't determine phone types for ["
          + [
            foundCell ? null : "cell",
            foundHome ? null : "home",
            foundWork ? null : "work"
          ].filter(Boolean).join(", ")
          + "]."
      );

      if (noMatch.length === phones.length && phones.length === 1) {
        borrower.cell_phone = phones[0].phone;
      }
    }
  }
}

export function importIdentificationVerification(obj, { borrower }) {
  // TODO spouse? would be in obj.SPOUSE_INFORMATION.NAME
}

export function importAddress(obj, { borrower }) {
  let address = obj.ADDRESS;
  if (!address) {
    return;
  }

  if (Array.isArray(address)) {
    console.warn(
      `There are ${address.length} addresses in the XML but we only support 1. `
        + `Going to use the first one.`
    );
    address = address[0];
  }

  for (const [key, name] of Object.entries(ADDRESS_PROPS)) {
    if (address[name]) {
      borrower[key] = address[name];
    }
  }

  if (address[ADDRESS_STATE_PROPS.state_code]) {
    borrower.state = address[ADDRESS_STATE_PROPS.state_code];
  } else if (address[ADDRESS_STATE_PROPS.state_name]) {
    borrower.state = address[ADDRESS_STATE_PROPS.state_name];
  }
}

export function importBorrowerDetail(obj, { borrower }) {
  if (obj.BorrowerBirthDate) {
    borrower.date_of_birth = obj.BorrowerBirthDate;
  } else if (obj.BirthDate) {
    borrower.date_of_birth = obj.BirthDate;
  }

  if (obj.MaritalStatusType) {
    for (const [id, label] of Object.entries(MARITAL_STATUS_ENUM)) {
      if (obj.MaritalStatusType === label) {
        borrower.marital_status = id;
        break;
      }
    }
  }

  if (mismoBool(obj.SelfDeclaredMilitaryServiceIndicator)) {
    borrower.is_veteran = YES;
  }
}

export function importCreditScoreDetail(obj, { borrower }) {
  const {
    CreditScoreValue: value
  } = obj;

  if (!value) {
    return;
  }

  // Scores sorted, asc.
  const scores = [...Object.entries(CREDIT_SCORE_VALUES)]
    .sort(([, minScoreA], [, minScoreB]) => minScoreA - minScoreB);

  let rangeId = null;

  for (const [id, minScore] of scores) {
    // If the actual is value is now lower than the min for this score range,
    // then the previous one is the match. Otherwise, we should keep going up
    // the list until we find that or get to the top.
    if (value < minScore) {
      break;
    } else {
      rangeId = id;
    }
  }

  if (rangeId) {
    borrower.lowest_credit_mid_score = rangeId;
  }
}

// A parsed MISMO text node is a plain string when it has no attributes, or an
// object of the form { "#": "value", "@attr": ... } when it does. Normalize to
// a trimmed string either way.
function creditNodeText(node) {
  if (node == null) {
    return "";
  }
  if (typeof node === "object") {
    return String(node["#"] || "").trim();
  }
  return String(node).trim();
}

// Map a numeric score to its range-bucket id (same buckets pricing uses).
function scoreToRangeId(value) {
  const scores = [...Object.entries(CREDIT_SCORE_VALUES)].sort(
    ([, minScoreA], [, minScoreB]) => minScoreA - minScoreB
  );

  let rangeId = null;
  for (const [id, minScore] of scores) {
    if (value < minScore) {
      break;
    }
    rangeId = id;
  }
  return rangeId;
}

const CREDIT_BUREAU_TO_FIELD = {
  equifax: "credit_score_equifax",
  experian: "credit_score_experian",
  transunion: "credit_score_transunion"
};

// Import the whole CREDIT_SCORES block (all three bureau CREDIT_SCORE nodes) in
// one pass. Captures each bureau's raw score, computes the borrower's
// representative (middle) score, and derives the range bucket that pricing
// keys off. This replaces the old per-node importer, which took only one score
// and let the last bureau win instead of taking the median.
export function importCreditScores(obj, { borrower }) {
  if (!obj) {
    return;
  }

  let list = obj.CREDIT_SCORE;
  if (!list) {
    return;
  }
  if (!Array.isArray(list)) {
    list = [list];
  }

  const values = [];
  for (const creditScore of list) {
    if (!creditScore) {
      continue;
    }
    const detail = creditScore.CREDIT_SCORE_DETAIL || {};
    const value = parseInt(creditNodeText(detail.CreditScoreValue), 10);
    if (!Number.isFinite(value)) {
      continue;
    }
    values.push(value);

    const source = creditNodeText(detail.CreditRepositorySourceType).toLowerCase();
    for (const [bureau, field] of Object.entries(CREDIT_BUREAU_TO_FIELD)) {
      if (source.includes(bureau)) {
        borrower[field] = String(value);
        break;
      }
    }
  }

  const mid = representative_mid_score(values);
  if (mid == null) {
    return;
  }

  borrower.credit_mid_score = String(mid);
  const rangeId = scoreToRangeId(mid);
  if (rangeId) {
    borrower.lowest_credit_mid_score = rangeId;
  }
}

export function importResidences(obj, { borrower }) {
  let residence = obj.RESIDENCE;
  if (!residence) {
    return;
  }

  if (Array.isArray(residence)) {
    console.warn(
      `There are ${residence.length} residences in the XML but we only support 1. `
        + `Going to use the first one.`
    );
    residence = residence[0];
  }

  const residencyType = getNestedValue(
    residence,
    "RESIDENCE_DETAIL.BorrowerResidencyBasisType"
  );

  if (residencyType === "Own") {
    borrower.currently_renting_or_owning = OWN;
  } else if (residencyType === "Rent") {
    borrower.currently_renting_or_owning = RENT;

    const residencyDurationMonths = getNestedValue(
      residence,
      "RESIDENCE_DETAIL.BorrowerResidencyDurationMonthsCount"
    );

    if (residencyDurationMonths !== undefined) {
      borrower.how_long_in_current_residence = residencyDurationMonths;
    }
  }

  importAddress(residence, { borrower });
}

export function importIncomeItemDetail(obj, { incomeOther }) {
  // Employment income has no "other income" row — it is applied to the
  // matching EMPLOYER instead (see CURRENT_INCOME_ITEM in mismo_config).
  if (!incomeOther) return;

  const {
    CurrentIncomeMonthlyTotalAmount: monthlyIncome,
    IncomeType: incomeType,
    IncomeTypeOtherDescription: description,
    SeasonalIncomeIndicator: seasonalIncome
  } = obj;

  if (monthlyIncome !== undefined) {
    incomeOther.yearly_amount = payment_to_year(monthlyIncome, 12);
  }

  if (description) {
    incomeOther.description = description;
  }

  let found = false;

  for (const [key, value] of Object.entries(INCOME_TYPE_ENUM)) {
    if (value === incomeType) {
      incomeOther.income_type = key;
      found = true;
      break;
    }
  }

  if (!found) {
    if (mismoBool(seasonalIncome)) {
      incomeOther.income_type = SEASONAL_INCOME;
    } else {
      incomeOther.income_type = OTHER_INCOME;
      if (incomeType) {
        // We couldn't find a income type math, so we set the type to other,
        // and prefix the desciption with the type string from MISMO if
        // there is one.
        const prefix = `${incomeType}${incomeOther.description ? " - " : ""}`
        incomeOther.description = `${prefix}${incomeOther.description}`;
      }
    }
  }
}

export function importEmployerContacts(obj, { incomeSource }) {
  if (!obj.CONTACT) {
    return;
  }

  const contacts = toArray(obj.CONTACT);
  let found = false;

  for (const contact of contacts) {
    const contactPoints = getNestedValue(
      contact,
      "CONTACT_POINTS.CONTACT_POINT"
    );

    if (!contactPoints) {
      continue;
    }

    const contactPointsArr = toArray(contactPoints);

    for (const contactPoint of contactPointsArr) {
      const phone = getNestedValue(
        contactPoint,
        "CONTACT_POINT_TELEPHONE.ContactPointTelephoneValue"
      );

      // Skip empty <ContactPointTelephoneValue/> (converts to {}, not "").
      if (typeof phone === "string" && phone.trim()) {
        incomeSource.employment_information.phone = phone;
        found = true;
        break;
      }
    }

    if (found) {
      break;
    }
  }
}

// Employer name. The export writes it here (and the Fannie 3.2 importer reads
// it), but the 3.4 import never did, so every imported employer came in nameless.
export function importEmployerName(obj, { incomeSource }) {
  if (obj.FullName) {
    incomeSource.employment_information.employer_name = String(obj.FullName);
  }
}

export function importEmployerAddress(addressObj, { incomeSource }) {
  const {
    AddressLineText,
    CityName,
    PostalCode,
    StateName,
    StateCode
    // TODO other potential fields:
    // AddressAdditionalLineText,
    // AddressFormatType,
    // AddressFormatTypeOtherDescription,
    // AddressType,
    // AddressTypeOtherDescription,
    // AddressUnitDesignatorType,
    // AddressUnitDesignatorTypeOtherDescription,
    // AddressUnitIdentifier,
    // AttentionToName,
    // CarrierRouteCode,
    // CountryCode,
    // CountryName,
    // CountyCode,
    // CountyName,
    // DeliveryPointBarCodeCheckValue,
    // DeliveryPointBarCodeValue,
    // HighwayContractRouteIdentifier,
    // MailStopCode,
    // PlusFourZipCode,
    // PostOfficeBoxIdentifier,
    // RuralRouteBoxIdentifier,
    // RuralRouteIdentifier,
    // StreetName,
    // StreetPostDirectionalText,
    // StreetPreDirectionalText,
    // StreetPrimaryNumberText,
    // StreetSuffixText
  } = addressObj;

  if (AddressLineText) {
    incomeSource.employment_information.address = AddressLineText;
  }
  if (CityName) {
    incomeSource.employment_information.city = CityName;
  }
  if (PostalCode) {
    incomeSource.employment_information.zip = PostalCode;
  }
  if (StateName || StateCode) {
    incomeSource.employment_information.state = StateName || StateCode;
  }
}

export function importEmployerEmployment(obj, { incomeSource }) {
  const {
    EmploymentBorrowerSelfEmployedIndicator: selfEmployed,
    EmploymentMonthlyIncomeAmount: monthlyIncome,
    EmploymentYearsOnJobCount: yearsOnJob,
    EmploymentTimeMonthsCount: timeAtEmployer,
    EmploymentPositionDescription: positionDescription,
    EmploymentStartDate: startDate,
    EmploymentTimeInLineOfWorkYearsCount: lineOfWorkYears,
    EmploymentTimeInLineOfWorkMonthsCount: lineOfWorkMonths,
    OwnershipInterestType: interestType,
    SpecialBorrowerEmployerRelationshipIndicator: specialEmployerRelationship
  } = obj;

  if (mismoBool(selfEmployed)) {
    // TODO can we tell if it's 1099 or not? (SELF_EMPLOYED_1099)
    incomeSource.w2_or_self_employed = SELF_EMPLOYED;
  } else if (selfEmployed) {
    // TODO is this a safe assumption?
    incomeSource.w2_or_self_employed = W2;
  }

  if (monthlyIncome !== undefined) {
    // TODO is this right?
    incomeSource.base_income_0 = payment_to_year(monthlyIncome, 12);
  }

  if (yearsOnJob !== undefined) {
    incomeSource.years_on_the_current_job = yearsOnJob;
  } else if (timeAtEmployer !== undefined) {
    const totalMonths = parseFloat(timeAtEmployer) || 0;
    incomeSource.years_on_the_current_job = String(Math.floor(totalMonths / 12));
    incomeSource.months_on_the_current_job = String(totalMonths % 12);
  }

  if (positionDescription) {
    incomeSource.employment_information.position = positionDescription;
  }

  if (startDate) {
    incomeSource.employment_information.start_date = startDate;
  }

  if (lineOfWorkYears !== undefined) {
    // Old LANIS format: years and months stored separately
    incomeSource.employment_information.line_of_work_years = String(lineOfWorkYears);
    if (lineOfWorkMonths !== undefined) {
      incomeSource.employment_information.line_of_work_months = String(lineOfWorkMonths);
    }
  } else if (lineOfWorkMonths !== undefined) {
    // POINT / new LANIS format: total months — split into years + months
    const totalMonths = parseFloat(lineOfWorkMonths) || 0;
    incomeSource.employment_information.line_of_work_years = String(Math.floor(totalMonths / 12));
    incomeSource.employment_information.line_of_work_months = String(totalMonths % 12);
  }

  for (const [id, label] of Object.entries(OWNERSHIP_ENUM)) {
    if (label === interestType) {
      incomeSource.employment_information.ownership = id;
      break;
    }
  }

  if (mismoBool(specialEmployerRelationship)) {
    incomeSource.employment_information.employed_by_party_to_transaction = YES;
  }
}

// SSN -> borrower.ssn (formatted XXX-XX-XXXX). Runs per TAXPAYER_IDENTIFIER.
export function importTaxpayerIdentifier(obj, { borrower }) {
  if (!borrower) return;
  if (
    obj.TaxpayerIdentifierType === "SocialSecurityNumber" &&
    obj.TaxpayerIdentifierValue
  ) {
    const digits = String(obj.TaxpayerIdentifierValue).replace(/\D/g, "");
    borrower.ssn =
      digits.length === 9
        ? `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
        : String(obj.TaxpayerIdentifierValue);
  }
}
